import PropTypes from "prop-types";
import React, { useState } from "react";
import { Button } from "src/@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/@/components/ui/dropdown-menu";
import { Play, Pause, Download, Loader2 } from "lucide-react";
import { useWavesurfer } from "src/hooks/useWavesurfer";
import useSettings from "src/hooks/useSettings";

const AudioComponent = ({ url, isMe, downloaded, downloadAudio, loading }) => {
  const [playbackRate, setPlaybackRate] = useState(1);
  const { themeMode } = useSettings();
  const isDark = themeMode === 'dark';

  const {
    containerRef,
    wavesurfer,
    isPlaying,
    currentTime,
    duration,
    isReady,
    playPause,
    setPlaybackRate: setWSPlaybackRate
  } = useWavesurfer({
    url: downloaded ? url : null,
    waveColor: isDark ? "#4a4d52" : "#d1d5db",
    progressColor: "#8b5cf6",
    cursorColor: "#8b5cf6",
    height: 30,
    barWidth: 2,
    barRadius: 2,
    barGap: 1,
    normalize: true,
    pixelRatio: 1
  });

  const playbackOptions = [
    { value: "1", label: "1x" },
    { value: "1.5", label: "1.5x" },
    { value: "2", label: "2x" },
    { value: "3", label: "3x" },
  ];

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleDownload = async () => {
    if (!downloadAudio) return;
    try {
      await downloadAudio("audio");
    } catch (error) {
      console.error("Error downloading audio:", error);
    }
  };

  const handleClick = async () => {
    if (!downloaded) {
      await handleDownload();
    } else {
      playPause();
    }
  };

  const handlePlaybackRateChange = (value) => {
    const rate = parseFloat(value);
    setPlaybackRate(rate);
    setWSPlaybackRate(rate);
  };

  // Se não estiver baixado, mostrar apenas o botão de download
  if (!downloaded) {
    return (
      <div className={`bg-white dark:bg-[#222529] rounded-xl p-3 shadow-md dark:shadow-none ${isMe ? "ml-auto" : "mr-auto"} w-fit max-w-xs w-full`}>
        <div className="flex items-center gap-3">
          {/* Botão de Download */}
          <Button
            onClick={handleDownload}
            disabled={loading}
            className="flex-shrink-0 w-8 h-8 bg-primary hover:bg-primary/80 rounded-full flex items-center justify-center transition-all shadow-md hover:shadow-lg"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            ) : (
              <Download className="w-4 h-4 text-white" fill="white" />
            )}
          </Button>

          {/* Espaçador para manter o tamanho */}
          <div className="flex-1 h-8" />

          {/* Texto indicativo */}
          <span className="text-xs text-gray-500 dark:text-[#ABABAD] font-medium">Baixe o áudio para ouvir</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-[#222529] rounded-xl p-3 shadow-md dark:shadow-none ${isMe ? "ml-auto" : "mr-auto"} w-full`}>
      <div className="flex items-center gap-3">
        {/* Botão Play/Pause */}
        <Button
          onClick={handleClick}
          disabled={loading}
          className="flex-shrink-0 w-8 h-8 bg-primary hover:bg-primary/80 rounded-full flex items-center justify-center transition-all shadow-md hover:shadow-lg"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : (
            isPlaying ? (
              <Pause className="w-4 h-4 text-white" fill="white" />
            ) : (
              <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
            )
          )}
        </Button>

        {/* O container precisa ter altura fixa para não "esticar" */}
        <div
          ref={containerRef}
          className="flex-1 min-w-[100px] max-w-[120px] h-8"
          style={{ lineHeight: 0 }}
        />

        {/* Tempo e Velocidade */}
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 text-xs text-gray-500 dark:text-[#ABABAD] font-medium min-w-[36px] text-right tabular-nums">
            {isPlaying ? formatTime(currentTime) : formatTime(duration)}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs hover:bg-muted/50 rounded-md">
                {playbackRate}x
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-16">
              {playbackOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handlePlaybackRateChange(option.value)}
                  className="text-xs justify-center"
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

AudioComponent.propTypes = {
  url: PropTypes.string,
  isMe: PropTypes.bool.isRequired,
  downloaded: PropTypes.bool,
  downloadAudio: PropTypes.func,
  loading: PropTypes.bool,
};

export default AudioComponent;
